import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { PulseIdentity } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isKitWatcher } from './kit-watcher';
import { queryClient, queryKeys } from './query-client';


export type WebPulse = PulseIdentity;

export type PulsesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; pulses: WebPulse[] };

async function fetchPulses(): Promise<WebPulse[]> {
  const res = await apiFetch(`${API_BASE}/api/pulses`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, 'pulses'));
  }
  const data = (await res.json()) as { pulses?: WebPulse[] };
  return (data.pulses || []).filter((pulse) => !isKitWatcher(pulse));
}

export function usePulses(enabled = true): { state: PulsesState; refresh: () => Promise<void> } {
  const query = useQuery({ queryKey: queryKeys.pulses, queryFn: fetchPulses, enabled });
  // A refetch error while we already hold a good list stays 'ready' (query.data survives) — only surface
  // an error before the first success, matching the old store's `kind !== 'ready'` guard.
  const state: PulsesState = query.data
    ? { kind: 'ready', pulses: query.data }
    : query.status === 'error'
      ? { kind: 'error', message: query.error instanceof Error ? query.error.message : String(query.error) }
      : { kind: 'loading' };
  const refresh = () => queryClient.refetchQueries({ queryKey: queryKeys.pulses }).then(() => undefined);
  return { state, refresh };
}

function getPulses(): WebPulse[] | undefined {
  return queryClient.getQueryData<WebPulse[]>(queryKeys.pulses);
}

// A pulse can be cached in two places at once: the account-wide list (queryKeys.pulses — the /pulses
// screen) and a workspace-scoped list (queryKeys.workspaceRoutines(id) — a workspace's routines grid
// and its "All routines" screen; see lib/workspace-routines.ts). An optimistic edit must reach whichever
// list is holding the pulse so the change shows on every open surface. Field edits are id-scoped, so
// mapping them across every list is safe — a list that doesn't hold the id is returned unchanged.
function eachPulseList(fn: (list: WebPulse[]) => WebPulse[]): void {
  const global = getPulses();
  if (global) queryClient.setQueryData<WebPulse[]>(queryKeys.pulses, fn(global));
  queryClient.setQueriesData<WebPulse[]>({ queryKey: ['workspace-routines'] }, (list) => (list ? fn(list) : list));
}

// The pulse as it currently sits in any cache — used to capture the prior value for rollback.
function findPulse(id: string): WebPulse | null {
  const global = getPulses();
  const inGlobal = global?.find((p) => p.id === id);
  if (inGlobal) return inGlobal;
  for (const [, list] of queryClient.getQueriesData<WebPulse[]>({ queryKey: ['workspace-routines'] })) {
    const found = list?.find((p) => p.id === id);
    if (found) return found;
  }
  return null;
}

// Optimistically map the given pulse across every cache, returning its prior value for rollback (or null
// if it isn't loaded anywhere).
function updateInCache(id: string, updater: (p: WebPulse) => WebPulse): WebPulse | null {
  const prev = findPulse(id);
  if (!prev) return null;
  eachPulseList((list) => list.map((p) => (p.id === id ? updater(p) : p)));
  return prev;
}

function replaceInCache(id: string, replacement: WebPulse): void {
  eachPulseList((list) => list.map((p) => (p.id === id ? replacement : p)));
}

function removeFromCache(id: string): WebPulse | null {
  const prev = findPulse(id);
  eachPulseList((list) => list.filter((p) => p.id !== id));
  return prev;
}

// Roll a deleted pulse back into the account-wide list and its own workspace list (never other
// workspaces' lists — a pulse belongs to a single workspace).
function restoreToCache(pulse: WebPulse): void {
  const global = getPulses();
  if (global && !global.some((p) => p.id === pulse.id)) {
    queryClient.setQueryData<WebPulse[]>(queryKeys.pulses, [pulse, ...global]);
  }
  if (pulse.workspaceId) {
    const key = queryKeys.workspaceRoutines(pulse.workspaceId);
    const list = queryClient.getQueryData<WebPulse[]>(key);
    if (list && !list.some((p) => p.id === pulse.id)) {
      queryClient.setQueryData<WebPulse[]>(key, [pulse, ...list]);
    }
  }
}

// Optimistic patch — applies the change locally, then PATCHes the server.
// On failure rolls back to the prior pulse and surfaces an error message
// to the caller so the UI can toast / inline-error it.
export async function patchPulse(
  pulse: WebPulse,
  updates: { paused?: boolean; name?: string; description?: string },
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!pulse.id) return { ok: false, message: 'Missing pulse id' };
  const id = pulse.id;
  const previous = updateInCache(id, (p) => ({ ...p, ...updates }));
  try {
    const res = await apiFetch(`${API_BASE}/api/pulses/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      if (previous) replaceInCache(id, previous);
      return { ok: false, message: await readErrorMessage(res, 'pulse') };
    }
    const data = (await res.json()) as { pulse?: WebPulse };
    if (data.pulse) replaceInCache(id, data.pulse);
    return { ok: true };
  } catch (err: unknown) {
    if (previous) replaceInCache(id, previous);
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export async function deletePulse(id: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const previous = removeFromCache(id);
  try {
    const res = await apiFetch(`${API_BASE}/api/pulses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      if (previous) restoreToCache(previous);
      return { ok: false, message: await readErrorMessage(res, 'pulse') };
    }
    return { ok: true };
  } catch (err: unknown) {
    if (previous) restoreToCache(previous);
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

// Same shape as the one in lib/tasks.ts — keeps a non-JSON response (e.g.,
// a 404 HTML page from the dev proxy hitting an undeployed route) from
// being dumped into the UI as the user-facing error string.
async function readErrorMessage(res: Response, label: string): Promise<string> {
  const fallback = `Couldn't load ${label} (${res.status})`;
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  if (text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      const msg = parsed.message || parsed.error;
      if (msg) return msg;
    } catch {
      // fall through to status-only fallback
    }
  }
  return fallback;
}
